/**
 * KAN-826: field "Организация" of the client card. Shows the linked company
 * (link to /companies/<id> and the city). "Изменить" opens a search over the
 * companies: picking one links it (PATCH client_company_id), "Создать
 * организацию" links a new name (PATCH company: the server finds or creates
 * it), "Отвязать" unlinks (client_company_id: null). Requests and access
 * live in hooks/useClientCompanies.
 */
import { useId, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Loader2, Pencil, Plus, Unlink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCanOpenPath } from '@/hooks/useApiAccess';
import {
  useClientCompanySearch,
  useLinkClientCompany,
  type ClientCompanyLinkPayload,
} from '@/hooks/useClientCompanies';
import type { ClientCompanyBrief } from '@/types/clientCompany';

interface ClientCompanyFieldProps {
  clientId: string;
  company: ClientCompanyBrief | null | undefined;
}

const OPTION_CLASS =
  'flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none';

export function ClientCompanyField({ clientId, company }: ClientCompanyFieldProps) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const listboxId = useId();
  const link = useLinkClientCompany(clientId);
  const search = useClientCompanySearch(text, editing);
  const canOpenCompany = useCanOpenPath(`/companies/${company?.id ?? ''}`);

  const typed = text.trim();
  const results = search.data?.results ?? [];
  const exactMatch = results.some((item) => item.name.trim().toLowerCase() === typed.toLowerCase());
  const showCreate = typed !== '' && !exactMatch;
  const showList = editing && (results.length > 0 || showCreate);

  const close = () => {
    setEditing(false);
    setText('');
  };

  const submit = (payload: ClientCompanyLinkPayload) => {
    link.mutate(payload, { onSuccess: close });
  };

  const onOptionKey = (event: KeyboardEvent<HTMLLIElement>, payload: ClientCompanyLinkPayload) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      submit(payload);
    }
  };

  return (
    <div data-testid="client-company-field" className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium leading-none">
        <Building2 className="h-4 w-4 text-gray-500" aria-hidden="true" />
        Организация
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {company ? (
          <span className="min-w-0 break-words">
            {canOpenCompany ? (
              <Link to={`/companies/${company.id}`} className="font-medium text-blue-700 hover:underline">
                {company.name}
              </Link>
            ) : (
              <span className="font-medium">{company.name}</span>
            )}
            {company.city && <span className="ml-2 text-sm text-gray-500">{company.city}</span>}
          </span>
        ) : (
          <span className="text-sm text-gray-500">Не указана</span>
        )}
        {link.canEdit && !editing && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              aria-label="Изменить организацию"
              title="Изменить организацию"
              onClick={() => setEditing(true)}
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
              <span className="ml-1 hidden sm:inline">Изменить</span>
            </Button>
            {company && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-gray-600"
                disabled={link.isPending}
                onClick={() => submit({ client_company_id: null })}
              >
                <Unlink className="mr-1 h-4 w-4" aria-hidden="true" />
                Отвязать
              </Button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Input
              role="combobox"
              aria-label="Организация"
              aria-expanded={showList}
              aria-controls={listboxId}
              aria-autocomplete="list"
              autoComplete="off"
              autoFocus
              placeholder="Название, ИНН или город"
              className="min-w-0"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') close();
              }}
            />
            {(search.isFetching || link.isPending) && (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" aria-label="Загрузка" />
            )}
            <Button type="button" variant="outline" size="sm" onClick={close}>
              Отмена
            </Button>
          </div>
          {showList && (
            <ul
              id={listboxId}
              role="listbox"
              aria-label="Найденные организации"
              className="max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-white p-1 shadow-sm"
            >
              {results.map((item) => {
                const payload = { client_company_id: item.id };
                return (
                  <li
                    key={item.id}
                    role="option"
                    aria-selected={item.id === company?.id}
                    tabIndex={0}
                    className={OPTION_CLASS}
                    onClick={() => submit(payload)}
                    onKeyDown={(e) => onOptionKey(e, payload)}
                  >
                    <span className="min-w-0 truncate">{item.name}</span>
                    {(item.city || item.inn) && (
                      <span className="shrink-0 text-xs text-gray-500">
                        {[item.city, item.inn].filter(Boolean).join(', ')}
                      </span>
                    )}
                  </li>
                );
              })}
              {showCreate && (
                <li
                  role="option"
                  aria-selected={false}
                  tabIndex={0}
                  className={`${OPTION_CLASS} text-blue-700`}
                  onClick={() => submit({ company: typed })}
                  onKeyDown={(e) => onOptionKey(e, { company: typed })}
                >
                  <Plus className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {`Создать организацию "${typed}"`}
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
